/**
 * Storage Adapter Interface
 * 存储适配器接口 - 支持 localStorage 与自托管 HTTP API 的统一抽象层
 */

import { HttpStorageAdapter } from './httpStorageAdapter';
import { isApiConfigured } from './apiClient';

type PendingSyncEntry = {
    key: string;
    updatedAt: string;
};

/**
 * 存储元数据
 */
export interface StorageMetadata {
    lastSync: string;
    version: string;
    adapter: 'localStorage' | 'http-api';
}

/**
 * 存储查询参数
 */
export interface StorageQueryParams {
    filter?: Record<string, any>; // 简单相等匹配
    search?: { field: string; term: string }; // 模糊搜索
    limit?: number;
    offset?: number;
    orderBy?: { field: string; direction: 'asc' | 'desc' };
}

/**
 * 存储查询结果
 */
export interface StorageQueryResult<T> {
    items: T[];
    total: number;
}

/**
 * 存储适配器接口
 * 定义所有存储后端必须实现的方法
 */
export interface StorageAdapter {
    /** 获取单个值 */
    get<T>(key: string): Promise<T | null>;

    /** 设置单个值 */
    set<T>(key: string, value: T): Promise<void>;

    /** 删除单个值 */
    delete(key: string): Promise<void>;

    /** 批量获取 */
    getMany<T>(keys: string[]): Promise<Map<string, T | null>>;

    /** 批量设置 */
    setMany<T>(entries: Map<string, T>): Promise<void>;

    /** 列出以指定前缀开头的所有键 */
    listKeys(prefix: string): Promise<string[]>;

    /** 获取存储元数据 */
    getMetadata(): Promise<StorageMetadata>;

    /** 清除所有数据 */
    clear(): Promise<void>;

    /** 添加单条记录（用于数组类数据） */
    addRecord<T>(key: string, record: any): Promise<T>;

    /** 更新单条记录 */
    updateRecord<T>(key: string, id: string, updates: Partial<T>): Promise<void>;

    /** 删除单条记录 */
    deleteRecord(key: string, id: string): Promise<void>;

    /** 通过食材 ID 查找食谱 */
    findRecipesByIngredient(ingredientId: string): Promise<any[]>;

    /** 查询并获取分页/筛选数据 */
    query<T>(key: string, params?: StorageQueryParams): Promise<StorageQueryResult<T>>;
}

/**
 * Key 命名规范
 * foodbook:{namespace}:{entity}:{id?}
 */
export const StorageKeys = {
    // 公共数据
    PUBLIC_RECIPES: 'foodbook:public:recipes',
    PUBLIC_INGREDIENTS: 'foodbook:public:ingredients',

    // 用户数据（需要替换 {userId}）
    userProfile: (userId: string) => `foodbook:user:${userId}:profile`,
    userSchedule: (userId: string) => `foodbook:user:${userId}:schedule`,
    userShopping: (userId: string) => `foodbook:user:${userId}:shopping`,
    userInventory: (userId: string) => `foodbook:user:${userId}:inventory`,

    // 用户自定义数据
    userRecipes: (userId: string) => `foodbook:user:${userId}:recipes:custom`,
    userIngredients: (userId: string) => `foodbook:user:${userId}:ingredients:custom`,
    userLogs: (userId: string) => `foodbook:user:${userId}:logs`,
    userFavorites: (userId: string) => `foodbook:user:${userId}:favorites`,

    // 用户档案数据
    userBasicInfo: (userId: string) => `foodbook:user:${userId}:basic_info`,
    userBodyData: (userId: string) => `foodbook:user:${userId}:body_data`,
    userDietaryPreferences: (userId: string) => `foodbook:user:${userId}:dietary_preferences`,
    userNutritionGoals: (userId: string) => `foodbook:user:${userId}:nutrition_goals`,
    userSettings: (userId: string) => `foodbook:user:${userId}:settings`,
    userAIConfig: (userId: string) => `foodbook:user:${userId}:ai_config`,

    // 关联索引
    INDEX_INGREDIENT_RECIPES: 'foodbook:index:ingredient-recipes',

    // 元数据
    METADATA: 'foodbook:meta',

    // 默认用户 ID
    // 使用 anonymous 避免应用启动早期误请求不存在的 "local" 用户私有键。
    DEFAULT_USER_ID: 'anonymous',
} as const;

/**
 * LocalStorage 适配器实现
 */
export class LocalStorageAdapter implements StorageAdapter {
    private prefix: string;

    constructor(prefix: string = '') {
        this.prefix = prefix;
    }

    private getFullKey(key: string): string {
        return this.prefix ? `${this.prefix}:${key}` : key;
    }

    async get<T>(key: string): Promise<T | null> {
        try {
            const fullKey = this.getFullKey(key);
            const data = localStorage.getItem(fullKey);
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.error(`[LocalStorage] Failed to get ${key}:`, error);
            return null;
        }
    }

    async set<T>(key: string, value: T): Promise<void> {
        try {
            const fullKey = this.getFullKey(key);
            console.log('[LocalStorageAdapter] Setting:', fullKey, 'Value:', value);
            localStorage.setItem(fullKey, JSON.stringify(value));
        } catch (error) {
            console.error(`[LocalStorage] Failed to set ${key}:`, error);
            throw error;
        }
    }

    async delete(key: string): Promise<void> {
        try {
            const fullKey = this.getFullKey(key);
            localStorage.removeItem(fullKey);
        } catch (error) {
            console.error(`[LocalStorage] Failed to delete ${key}:`, error);
            throw error;
        }
    }

    async getMany<T>(keys: string[]): Promise<Map<string, T | null>> {
        const results = new Map<string, T | null>();
        for (const key of keys) {
            results.set(key, await this.get<T>(key));
        }
        return results;
    }

    async setMany<T>(entries: Map<string, T>): Promise<void> {
        for (const [key, value] of entries) {
            await this.set(key, value);
        }
    }

    async listKeys(prefix: string): Promise<string[]> {
        const fullPrefix = this.getFullKey(prefix);
        const keys: string[] = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(fullPrefix)) {
                // 移除 prefix 返回原始 key
                keys.push(this.prefix ? key.replace(`${this.prefix}:`, '') : key);
            }
        }

        return keys;
    }

    async getMetadata(): Promise<StorageMetadata> {
        const saved = await this.get<StorageMetadata>(StorageKeys.METADATA);
        return saved || {
            lastSync: new Date().toISOString(),
            version: '1.0.0',
            adapter: 'localStorage',
        };
    }

    async clear(): Promise<void> {
        const keysToRemove: string[] = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(this.prefix || 'foodbook:')) {
                keysToRemove.push(key);
            }
        }

        keysToRemove.forEach(key => localStorage.removeItem(key));
    }

    async addRecord<T>(key: string, record: any): Promise<T> {
        const items = await this.get<any[]>(key) || [];
        const newRecord = { ...record, id: record.id || `local-${Date.now()}` };
        items.push(newRecord);
        await this.set(key, items);
        return newRecord as T;
    }

    async updateRecord<T>(key: string, id: string, updates: Partial<T>): Promise<void> {
        const items = await this.get<any[]>(key) || [];
        const index = items.findIndex(item => item.id === id);
        if (index !== -1) {
            items[index] = { ...items[index], ...updates };
            await this.set(key, items);
        }
    }

    async deleteRecord(key: string, id: string): Promise<void> {
        const items = await this.get<any[]>(key) || [];
        const filtered = items.filter(item => item.id !== id);
        await this.set(key, filtered);
    }

    async findRecipesByIngredient(ingredientId: string): Promise<any[]> {
        // Naive implementation for localStorage: scan public recipes
        const recipes = await this.get<any[]>(StorageKeys.PUBLIC_RECIPES) || [];
        return recipes.filter(r =>
            r.ingredients?.some((i: any) => i.linkedIngredientId === ingredientId)
        );
    }

    async query<T>(key: string, params?: StorageQueryParams): Promise<StorageQueryResult<T>> {
        let items = await this.get<T[]>(key) || [];

        // 1. Filter
        if (params?.filter) {
            items = items.filter((item: any) => {
                return Object.entries(params.filter!).every(([k, v]) => item[k] === v);
            });
        }

        // 2. Search
        if (params?.search) {
            const { field, term } = params.search;
            const lowerTerm = term.toLowerCase();
            items = items.filter((item: any) => {
                const val = item[field];
                return val && String(val).toLowerCase().includes(lowerTerm);
            });
        }

        // 3. OrderBy
        if (params?.orderBy) {
            const { field, direction } = params.orderBy;
            items.sort((a: any, b: any) => {
                const valA = a[field];
                const valB = b[field];
                if (valA < valB) return direction === 'asc' ? -1 : 1;
                if (valA > valB) return direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        const total = items.length;

        // 4. Pagination
        if (params?.offset !== undefined || params?.limit !== undefined) {
            const offset = params.offset || 0;
            const limit = params.limit !== undefined ? params.limit : total;
            items = items.slice(offset, offset + limit);
        }

        return { items, total };
    }
}

/**
 * 离线优先存储适配器
 * - 读取优先走本地，未命中时回退到自托管后端并回填本地
 * - 写入先落本地，再异步镜像到自托管后端
 * - 云端写失败时记录待同步 key，后续在在线状态重试
 */
export class OfflineFirstStorageAdapter implements StorageAdapter {
    private static readonly PENDING_SYNC_KEY = 'foodbook:sync:pending-keys';

    private flushTimer: ReturnType<typeof setTimeout> | null = null;
    private flushInProgress = false;

    constructor(
        private localAdapter: StorageAdapter,
    private remoteAdapter: StorageAdapter,
        private syncDelayMs = 1200
    ) {}

    private isOnline(): boolean {
        return typeof navigator === 'undefined' || navigator.onLine !== false;
    }

    private isRecoverableRemoteError(error: unknown): boolean {
        const status = typeof error === 'object' && error !== null
            ? (error as { details?: { status?: number; originalError?: { status?: number } } }).details?.status
                ?? (error as { details?: { originalError?: { status?: number } } }).details?.originalError?.status
            : undefined;
        if (status === 401) {
            return true;
        }

        const message = error instanceof Error
            ? error.message
            : typeof error === 'string'
                ? error
                : String(error);
        const normalized = message.toLowerCase();

        return normalized.includes('network') ||
            normalized.includes('unauthorized') ||
            normalized.includes('fetch') ||
            normalized.includes('cors') ||
            normalized.includes('preflight') ||
            normalized.includes('access-control-allow-origin') ||
            normalized.includes("reading 'headers'") ||
            normalized.includes('数据库操作失败: get') ||
            normalized.includes('数据库操作失败: query');
    }

    private getPendingEntries(): PendingSyncEntry[] {
        if (typeof localStorage === 'undefined') return [];

        try {
            const raw = localStorage.getItem(OfflineFirstStorageAdapter.PENDING_SYNC_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private savePendingEntries(entries: PendingSyncEntry[]): void {
        if (typeof localStorage === 'undefined') return;

        try {
            localStorage.setItem(
                OfflineFirstStorageAdapter.PENDING_SYNC_KEY,
                JSON.stringify(entries)
            );
        } catch {
            // 忽略队列写入失败，避免影响主流程
        }
    }

    private markPending(key: string): void {
        const entries = this.getPendingEntries();
        const filtered = entries.filter(entry => entry.key !== key);
        filtered.push({ key, updatedAt: new Date().toISOString() });
        this.savePendingEntries(filtered);
    }

    private clearPending(key: string): void {
        const entries = this.getPendingEntries().filter(entry => entry.key !== key);
        this.savePendingEntries(entries);
    }

    private scheduleFlush(): void {
        if (!this.isOnline()) return;
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
        }

        this.flushTimer = setTimeout(() => {
            void this.flushPendingMutations();
        }, this.syncDelayMs);
    }

    async flushPendingMutations(): Promise<void> {
        if (this.flushInProgress || !this.isOnline()) return;

        this.flushInProgress = true;
        try {
            const pendingEntries = this.getPendingEntries()
                .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt));

            for (const entry of pendingEntries) {
                const localValue = await this.localAdapter.get(entry.key);
                if (localValue === null) {
                    await this.remoteAdapter.delete(entry.key);
                } else {
                    await this.remoteAdapter.set(entry.key, localValue);
                }
                this.clearPending(entry.key);
            }
        } catch (error) {
            console.warn('[OfflineFirstStorageAdapter] Failed to flush pending mutations:', error);
        } finally {
            this.flushInProgress = false;
        }
    }

    private async mirrorWrite(key: string): Promise<void> {
        this.markPending(key);
        this.scheduleFlush();
    }

    async get<T>(key: string): Promise<T | null> {
        const localValue = await this.localAdapter.get<T>(key);
        if (localValue !== null) {
            this.scheduleFlush();
            return localValue;
        }

        if (!this.isOnline()) {
            return null;
        }

        try {
            const remoteValue = await this.remoteAdapter.get<T>(key);
            if (remoteValue !== null) {
                await this.localAdapter.set(key, remoteValue);
                this.clearPending(key);
            }
            return remoteValue;
        } catch (error) {
            if (!this.isRecoverableRemoteError(error)) {
                throw error;
            }

            console.warn('[OfflineFirstStorageAdapter] Remote read failed, falling back to local cache:', key, error);
            return null;
        }
    }

    async set<T>(key: string, value: T): Promise<void> {
        await this.localAdapter.set(key, value);
        await this.mirrorWrite(key);
    }

    async delete(key: string): Promise<void> {
        await this.localAdapter.delete(key);
        await this.mirrorWrite(key);
    }

    async getMany<T>(keys: string[]): Promise<Map<string, T | null>> {
        const results = new Map<string, T | null>();
        for (const key of keys) {
            results.set(key, await this.get<T>(key));
        }
        return results;
    }

    async setMany<T>(entries: Map<string, T>): Promise<void> {
        for (const [key, value] of entries) {
            await this.set(key, value);
        }
    }

    async listKeys(prefix: string): Promise<string[]> {
        return this.localAdapter.listKeys(prefix);
    }

    async getMetadata(): Promise<StorageMetadata> {
        const metadata = await this.localAdapter.getMetadata();
        return {
            ...metadata,
            adapter: 'localStorage',
            lastSync: this.getPendingEntries().length === 0
                ? metadata.lastSync
                : new Date().toISOString(),
        };
    }

    async clear(): Promise<void> {
        await this.localAdapter.clear();
        this.savePendingEntries([]);
    }

    async addRecord<T>(key: string, record: any): Promise<T> {
        const added = await this.localAdapter.addRecord<T>(key, record);
        await this.mirrorWrite(key);
        return added;
    }

    async updateRecord<T>(key: string, id: string, updates: Partial<T>): Promise<void> {
        await this.localAdapter.updateRecord(key, id, updates);
        await this.mirrorWrite(key);
    }

    async deleteRecord(key: string, id: string): Promise<void> {
        await this.localAdapter.deleteRecord(key, id);
        await this.mirrorWrite(key);
    }

    async findRecipesByIngredient(ingredientId: string): Promise<any[]> {
        const localMatches = await this.localAdapter.findRecipesByIngredient(ingredientId);
        if (localMatches.length > 0 || !this.isOnline()) {
            return localMatches;
        }

        try {
            return await this.remoteAdapter.findRecipesByIngredient(ingredientId);
        } catch (error) {
            if (!this.isRecoverableRemoteError(error)) {
                throw error;
            }

            console.warn('[OfflineFirstStorageAdapter] Remote recipe lookup failed, falling back to local cache:', ingredientId, error);
            return localMatches;
        }
    }

    async query<T>(key: string, params?: StorageQueryParams): Promise<StorageQueryResult<T>> {
        const localResult = await this.localAdapter.query<T>(key, params);
        if (localResult.total > 0 || !this.isOnline()) {
            this.scheduleFlush();
            return localResult;
        }

        try {
            return await this.remoteAdapter.query<T>(key, params);
        } catch (error) {
            if (!this.isRecoverableRemoteError(error)) {
                throw error;
            }

            console.warn('[OfflineFirstStorageAdapter] Remote query failed, falling back to local cache:', key, error);
            return localResult;
        }
    }
}

const getEnvVar = (key: string, defaultValue: string = ''): string => {
    try {
        if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
            return (import.meta as any).env[key] || defaultValue;
        }
    } catch {
        // 忽略错误
    }
    if (typeof window !== 'undefined' && (window as any).__ENV__) {
        return (window as any).__ENV__[key] || defaultValue;
    }
    return defaultValue;
};

/**
 * 创建存储适配器实例
 * 根据环境变量或配置选择适配器
 */
export function createStorageAdapter(): StorageAdapter {
    const storageMode = getEnvVar('VITE_STORAGE_MODE', 'offline-first');
    const localAdapter = new LocalStorageAdapter('');
    const cloudAdapter = new HttpStorageAdapter();

    if (storageMode === 'http-api') {
        return cloudAdapter;
    }

    if (storageMode === 'local-only') {
        return localAdapter;
    }

    if (!isApiConfigured()) {
        return localAdapter;
    }

    return new OfflineFirstStorageAdapter(localAdapter, cloudAdapter);
}

// 导出默认存储适配器实例
export const storageAdapter = createStorageAdapter();
